import React, { useState, useEffect } from 'react';
import { useLibrary } from '../context/LibraryContext';
import { usePlayer } from '../context/PlayerContext';
import { FaPlay, FaPlus, FaEllipsisH, FaFilter, FaTimes, FaFileDownload } from 'react-icons/fa';
import axios from 'axios';
import '../styles/Library.css';

const Library = () => {
  const { songs, loading, error, fetchSongs } = useLibrary();
  const [exporting, setExporting] = useState(false);
  const [selectedSongs, setSelectedSongs] = useState([]);
  const { playSong } = usePlayer();
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    bpm: '',
    key: '',
    genre: '',
    tags: ''
  });
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Unique values for filters
  const [uniqueGenres, setUniqueGenres] = useState([]);
  const [uniqueKeys, setUniqueKeys] = useState([]);
  const [uniqueTags, setUniqueTags] = useState([]);

  useEffect(() => {
    fetchSongs();
  }, []);

  useEffect(() => {
    if (songs.length > 0) {
      // Extract unique values for filters
      const genres = [...new Set(songs.map(song => song.genre))];
      const keys = [...new Set(songs.map(song => song.key))];
      const tags = [...new Set(songs.flatMap(song => song.tags || []))];
      
      setUniqueGenres(genres);
      setUniqueKeys(keys);
      setUniqueTags(tags);
      
      // Apply filters and search
      applyFiltersAndSearch();
    }
  }, [songs, searchTerm, filters, sortBy, sortOrder]);

  const applyFiltersAndSearch = () => {
    let result = [...songs];
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(song => 
        song.title.toLowerCase().includes(term) || 
        song.artist.toLowerCase().includes(term)
      );
    }
    
    // Apply filters
    if (filters.bpm) {
      const bpm = parseInt(filters.bpm);
      result = result.filter(song => 
        song.bpm >= bpm - 5 && song.bpm <= bpm + 5
      );
    }
    
    if (filters.key) {
      result = result.filter(song => song.key === filters.key);
    }
    
    if (filters.genre) {
      result = result.filter(song => song.genre === filters.genre);
    }
    
    if (filters.tags) {
      const tags = filters.tags.split(',').map(tag => tag.trim());
      result = result.filter(song => 
        song.tags && tags.some(tag => song.tags.includes(tag))
      );
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'artist':
          comparison = a.artist.localeCompare(b.artist);
          break;
        case 'bpm':
          comparison = a.bpm - b.bpm;
          break;
        case 'key':
          comparison = a.key.localeCompare(b.key);
          break;
        case 'genre':
          comparison = a.genre.localeCompare(b.genre);
          break;
        case 'date':
        default:
          comparison = new Date(b.createdAt) - new Date(a.createdAt);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    setFilteredSongs(result);
  };

  const handlePlaySong = (song) => {
    playSong(song, filteredSongs, filteredSongs.findIndex(s => s._id === song._id));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      bpm: '',
      key: '',
      genre: '',
      tags: ''
    });
    setSearchTerm('');
  };

  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };
  
  // Fonction pour gérer la sélection/désélection d'une chanson pour l'exportation
  const toggleSongSelection = (songId) => {
    setSelectedSongs(prev => {
      if (prev.includes(songId)) {
        return prev.filter(id => id !== songId);
      } else {
        return [...prev, songId];
      }
    });
  };
  
  // Fonction pour exporter les chansons sélectionnées
  const handleExportClick = async () => {
    if (selectedSongs.length === 0) {
      alert('Veuillez sélectionner au moins une chanson à exporter.');
      return;
    }
    
    // Demander confirmation avant d'exporter
    const confirmMessage = selectedSongs.length === 1 
      ? 'Voulez-vous exporter 1 chanson?' 
      : `Voulez-vous exporter ${selectedSongs.length} chansons?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    setExporting(true);
    
    try {
      console.log('Début de l\'exportation des chansons:', selectedSongs);
      
      // Appel à l'API pour préparer les fichiers à exporter
      console.log('Envoi de la requête d\'exportation pour', selectedSongs.length, 'chansons');
      
      // Version simplifiée de la requête d'exportation
      const response = await axios({
        method: 'POST',
        url: '/api/songs/export',
        data: { songIds: selectedSongs },
        responseType: 'blob',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/zip'
        },
        timeout: 30000
      });
      
      // Vérifier si la réponse est une erreur
      if (response.status >= 400) {
        // Tenter de lire l'erreur comme du JSON
        const reader = new FileReader();
        reader.onload = function() {
          try {
            const errorData = JSON.parse(reader.result);
            alert(`Erreur: ${errorData.msg || errorData.error || 'Erreur lors de l\'exportation'}`);
          } catch (e) {
            alert(`Erreur ${response.status}: Erreur lors de l\'exportation des chansons.`);
          }
        };
        reader.readAsText(response.data);
        return;
      }
      
      // Vérifier que nous avons bien reçu un fichier non vide
      if (response.data.size === 0) {
        throw new Error('Aucune donnée reçue du serveur');
      }
      
      // Créer un URL pour le blob reçu
      const url = window.URL.createObjectURL(new Blob([response.data], {type: 'application/zip'}));
      
      // Créer un lien de téléchargement et cliquer dessus automatiquement
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `harmoniq_songs_${new Date().toISOString().slice(0,10)}.zip`);
      document.body.appendChild(link);
      link.click();
      
      // Nettoyer
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      }, 200);
      
      // Réinitialiser la sélection après l'exportation
      setSelectedSongs([]);
      setTimeout(() => {
        alert(`Exportation réussie de ${selectedSongs.length} chanson(s)!`);
      }, 500);
    } catch (error) {
      console.error('Erreur lors de l\'exportation des chansons:', error);
      alert(`Erreur lors de l\'exportation des chansons: ${error.message || 'Erreur inconnue'}. Veuillez réessayer.`);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="library-page">
      <div className="library-header">
        <h1>Ma Bibliothèque</h1>
        <div className="library-actions">
          {/* Bouton d'exportation des chansons */}
          <button 
            className="btn-export" 
            onClick={handleExportClick}
            disabled={exporting || selectedSongs.length === 0}
            aria-label="Exporter les chansons sélectionnées"
          >
            <FaFileDownload /> 
            {exporting ? 'Exportation...' : 'Exporter'}
            {selectedSongs.length > 0 && !exporting && (
              <span className="selection-counter">{selectedSongs.length}</span>
            )}
          </button>
          <button 
            className="btn-filter" 
            onClick={() => setShowFilters(!showFilters)}
            aria-label="Toggle filters"
          >
            <FaFilter /> Filtres
          </button>
        </div>
      </div>
      
      <div className="search-bar">
        <input
          type="text"
          placeholder="Rechercher par titre ou artiste..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {(searchTerm || Object.values(filters).some(v => v)) && (
          <button className="clear-search" onClick={clearFilters}>
            <FaTimes />
          </button>
        )}
      </div>
      
      {showFilters && (
        <div className="filters-panel">
          <div className="filters-grid">
            <div className="filter-group">
              <label htmlFor="bpm">BPM</label>
              <input
                type="number"
                id="bpm"
                name="bpm"
                placeholder="ex: 120"
                value={filters.bpm}
                onChange={handleFilterChange}
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="key">Tonalité</label>
              <select
                id="key"
                name="key"
                value={filters.key}
                onChange={handleFilterChange}
              >
                <option value="">Toutes</option>
                {uniqueKeys.map(key => (
                  <option key={key} value={key}>{key}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="genre">Genre</label>
              <select
                id="genre"
                name="genre"
                value={filters.genre}
                onChange={handleFilterChange}
              >
                <option value="">Tous</option>
                {uniqueGenres.map(genre => (
                  <option key={genre} value={genre}>{genre}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label htmlFor="tags">Tags</label>
              <input
                type="text"
                id="tags"
                name="tags"
                placeholder="ex: chill, été"
                value={filters.tags}
                onChange={handleFilterChange}
              />
            </div>
          </div>
          
          <div className="sort-options">
            <label htmlFor="sortBy">Trier par:</label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="date">Date d'ajout</option>
              <option value="title">Titre</option>
              <option value="artist">Artiste</option>
              <option value="bpm">BPM</option>
              <option value="key">Tonalité</option>
              <option value="genre">Genre</option>
            </select>
            
            <button 
              className="btn-sort-order" 
              onClick={toggleSortOrder}
              aria-label={sortOrder === 'asc' ? 'Trier par ordre décroissant' : 'Trier par ordre croissant'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="loading">Chargement de votre bibliothèque...</div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : filteredSongs.length === 0 ? (
        <div className="empty-library">
          <p>Aucun morceau ne correspond à votre recherche.</p>
          {(searchTerm || Object.values(filters).some(v => v)) && (
            <button className="btn btn-secondary" onClick={clearFilters}>
              Effacer les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="songs-table-container">
          <table className="songs-table">
            <thead>
              <tr>
                <th>
                  <input 
                    type="checkbox" 
                    className="select-all-checkbox"
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedSongs(filteredSongs.map(song => song._id));
                      } else {
                        setSelectedSongs([]);
                      }
                    }}
                    checked={selectedSongs.length > 0 && selectedSongs.length === filteredSongs.length}
                    aria-label="Sélectionner toutes les chansons"
                  />
                </th>
                <th></th>
                <th>Titre</th>
                <th>Artiste</th>
                <th>BPM</th>
                <th>Tonalité</th>
                <th>Genre</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSongs.map(song => (
                <tr key={song._id} className={`song-row ${selectedSongs.includes(song._id) ? 'selected' : ''}`}>
                  <td>
                    <input
                      type="checkbox"
                      className="song-checkbox"
                      checked={selectedSongs.includes(song._id)}
                      onChange={() => toggleSongSelection(song._id)}
                      aria-label="Sélectionner pour l'exportation"
                    />
                  </td>
                  <td>
                    <button 
                      className="btn-play" 
                      onClick={() => handlePlaySong(song)}
                      aria-label="Jouer"
                    >
                      <FaPlay />
                    </button>
                  </td>
                  <td>{song.title}</td>
                  <td>{song.artist}</td>
                  <td>{song.bpm}</td>
                  <td>{song.key}</td>
                  <td>{song.genre}</td>
                  <td>
                    <div className="song-actions">
                      <button 
                        className="btn-action" 
                        aria-label="Ajouter à une playlist"
                      >
                        <FaPlus />
                      </button>
                      <button 
                        className="btn-action" 
                        aria-label="Plus d'options"
                      >
                        <FaEllipsisH />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Library;
